import { useNotification } from 'components/Notification';
import { useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { useEventTracking } from 'state/eventTracking';
import { useModalControls } from 'state/modals';
import { ownedPodcastQueries } from '../state/queryKeys';
import { useGetPodcastOwnership } from '../state/useGetPodcastOwnership';

interface UseOwnershipResultConfig {
  podcastId: string | undefined;
}

export default function useOwnershipResult({
  podcastId,
}: UseOwnershipResultConfig) {
  const { open } = useModalControls();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { trackClaimPodcastOwnershipGranted } = useEventTracking();

  const { isLoading, data: ownershipResult } = useGetPodcastOwnership(
    podcastId,
    {
      onError: () =>
        showError(
          'There was an error checking the podcast owner. Please try again.',
        ),
    },
  );

  useEffect(() => {
    if (ownershipResult && podcastId) {
      // if user already owns podcast refetch owned podcasts data
      if (ownershipResult.claimStatus === 'success') {
        trackClaimPodcastOwnershipGranted();
        queryClient.invalidateQueries(ownedPodcastQueries.all);
        showSuccess("You've claimed this podcast!");
      } else if (ownershipResult?.podcastOwnerEmail === null) {
        // if the rss feed doesn't have an email address open configure feed modal
        open({
          name: 'ConfigureFeed',
        });
      } else if (ownershipResult.podcastOwnerEmail !== null) {
        // if the rss feed email doesn't match the user email, open claim ownership modal
        open({
          name: 'ClaimPodcastOwnership',
          params: {
            podcastId,
          },
        });
      }
    }
  }, [
    open,
    ownershipResult,
    podcastId,
    queryClient,
    showSuccess,
    trackClaimPodcastOwnershipGranted,
  ]);

  return { isLoading };
}
